<template>
    <div class="flex items-center justify-center" :class="{'stretch-layout h-full': props.fill}">
        <div v-if="props.pending" class="w-full" :class="{'stretch-layout h-full': props.fill && $slots.loading}">
            <slot name="loading">
				<loader :height="props.height" :color="props.color"/>
			</slot>
        </div>
        <card v-else-if="errorMessage" class="w-full border-box" :flat="props.flat" :no-spacing="props.flat" :class="{'stretch-layout': props.fill}">
            <alert type="warning" text no-spacing :content="errorMessage" :class="{'flex-grow-1': props.fill}" :icon="alertIcon">
                <template #button>
					<btn v-if="(statusCode === 403 || statusCode === 401) && !$auth.loggedIn" :to="$spoferanUrl('login')" color="warning lighten" class="min-w-200 w-full">
						<span>{{ $t('action.login') }}</span>
					</btn>
                    <btn v-else-if="statusCode >= 400 && statusCode < 500" @click="$router.back()" color="warning lighten" class="min-w-200 w-full">
                        <Icon :icon="icons.back"/>
                        <span>{{ $t('action.back') }}</span>
                    </btn>
                    <btn v-else @click="retry" color="warning lighten" class="min-w-200 w-full">
                        <Icon :icon="icons.refresh"/>
                        <span>{{ $t('action.try_again') }}</span>
                    </btn>
                </template>
            </alert>
        </card>
    </div>
</template>

<script setup lang="ts">
    import {isString} from "@spoferan/spoferan-ts-core";
	import {backIcon, refreshIcon, warningIcon} from "../../config";
	import {useI18n} from "#imports";
	import {useOnline} from "@vueuse/core";
	import {watch, computed} from "vue";
	import {mdiLockOutline} from "@mdi/js";

	const props = defineProps<{
		pending?: boolean,
		error?: any,
		height?: string | number,
		fill?: boolean,
		color?: string,

		// Whether a flat loader shall be displayed
		flat?: boolean,
	}>();

	const emit = defineEmits(['retry']);

	const {t} = useI18n();

	const icons = {
		back: backIcon,
		refresh: refreshIcon,
	};

    const DEFAULT_ERROR_MESSAGE = 'Request failed with status code 404';

	const isOnline = useOnline();

	const errorMessage = computed(() => {
		if (props.error?.display_message) {
			return props.error.display_message;
		}

		if (!isOnline.value) {
			return t('error.offline.message')
		}

		const errorMessage = props.error?.message && isString(props.error.message) ? props.error.message : null;

		// Handle default 404
		if (errorMessage === DEFAULT_ERROR_MESSAGE) {
			return t('error.404.message')
		}

		return errorMessage ?? t('error.loading')
	});

	const statusCode = computed(() => {
		if (props.error?.statusCode) {
			return props.error.statusCode;
		}
		if (props.error?.message === DEFAULT_ERROR_MESSAGE) {
			return 404;
		}

		if (!isOnline.value) {
			return 503;
		}

		return 500
	});

	const alertIcon = computed(() => {
		if (statusCode.value === 403) {
			return mdiLockOutline
		}

		return warningIcon
	});

	function retry() {
		emit('retry');
	}

	watch(isOnline, (newValue) => {
		if (newValue) {
			retry();
		}
	});
</script>
